import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Key, 
  BarChart3, 
  Plus, 
  Copy, 
  Trash2, 
  Eye,
  Calendar,
  Zap,
  Users,
  TrendingUp
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useAuthStore } from '@/stores/authStore'
import { useToast } from '@/hooks/use-toast'
import { api } from '@/services/api'
import { formatNumber, formatDate, formatDateTime } from '@/lib/utils'

interface ApiKey {
  id: string
  name: string
  key: string
  createdAt: string
  lastUsed?: string
  requestCount: number
}

interface DashboardStats {
  totalApiKeys: number
  totalRequests: number
  activeUsers: number
  uptime: number
}

export function DashboardPage() {
  const { user } = useAuthStore()
  const { toast } = useToast()
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [stats, setStats] = useState<DashboardStats>({
    totalApiKeys: 0,
    totalRequests: 0,
    activeUsers: 0,
    uptime: 0,
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [apiKeysResponse, statsResponse] = await Promise.all([
          api.getApiKeys(),
          api.getDashboardStats(),
        ])
        
        setApiKeys(apiKeysResponse.data)
        setStats(statsResponse.data)
      } catch (error: any) {
        toast({
          title: 'Error loading dashboard',
          description: error.message || 'Failed to load dashboard data',
          variant: 'destructive',
        })
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [toast])

  const handleCopyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key)
      toast({
        title: 'API key copied',
        description: 'API key has been copied to clipboard',
      })
    } catch (error) {
      toast({
        title: 'Copy failed',
        description: 'Failed to copy API key to clipboard',
        variant: 'destructive',
      })
    }
  }

  const handleDeleteKey = async (keyId: string) => {
    try {
      await api.deleteApiKey(keyId)
      setApiKeys(apiKeys.filter(key => key.id !== keyId))
      toast({
        title: 'API key deleted',
        description: 'API key has been successfully deleted',
      })
    } catch (error: any) {
      toast({
        title: 'Delete failed',
        description: error.message || 'Failed to delete API key',
        variant: 'destructive',
      })
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Welcome back, {user?.name}!
            </h1>
            <p className="text-muted-foreground">
              Here's what's happening with your API keys and usage.
            </p>
          </div>
          <Button asChild>
            <Link to="/api-keys">
              <Plus className="mr-2 h-4 w-4" />
              Create API Key
            </Link>
          </Button>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[
          {
            title: 'Total API Keys',
            value: stats.totalApiKeys,
            icon: Key,
            change: '+12%',
            changeType: 'positive' as const,
          },
          {
            title: 'Total Requests',
            value: formatNumber(stats.totalRequests),
            icon: Zap,
            change: '+23%',
            changeType: 'positive' as const,
          },
          {
            title: 'Active Users',
            value: stats.activeUsers,
            icon: Users,
            change: '+5%',
            changeType: 'positive' as const,
          },
          {
            title: 'Uptime',
            value: `${stats.uptime}%`,
            icon: TrendingUp,
            change: '+0.1%',
            changeType: 'positive' as const,
          },
        ].map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {stat.title}
                </CardTitle>
                <stat.icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground">
                  <span className={`inline-flex items-center ${
                    stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    <TrendingUp className="h-3 w-3 mr-1" />
                    {stat.change}
                  </span>
                  {' '}from last month
                </p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Recent API Keys */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Recent API Keys</CardTitle>
                <CardDescription>
                  Your recently created API keys and their usage
                </CardDescription>
              </div>
              <Button variant="outline" asChild>
                <Link to="/api-keys">View All</Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {apiKeys.length === 0 ? (
              <div className="text-center py-8">
                <Key className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-medium">No API keys yet</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Get started by creating your first API key.
                </p>
                <Button className="mt-4" asChild>
                  <Link to="/api-keys">
                    <Plus className="mr-2 h-4 w-4" />
                    Create API Key
                  </Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {apiKeys.slice(0, 5).map((apiKey, index) => (
                  <motion.div
                    key={apiKey.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Key className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium">{apiKey.name}</h4>
                        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                          <span className="flex items-center">
                            <Calendar className="mr-1 h-3 w-3" />
                            Created: {formatDate(apiKey.createdAt)}
                          </span>
                          {apiKey.lastUsed && (
                            <span className="flex items-center">
                              <Eye className="mr-1 h-3 w-3" />
                              Last used: {formatDateTime(apiKey.lastUsed)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-muted-foreground">
                        {formatNumber(apiKey.requestCount)} requests
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleCopyKey(apiKey.key)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteKey(apiKey.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="cursor-pointer hover:shadow-md transition-shadow">
            <Link to="/api-keys" className="block p-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Key className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Manage API Keys</h3>
                  <p className="text-sm text-muted-foreground">
                    Create, view, and manage your API keys
                  </p>
                </div>
              </div>
            </Link>
          </Card>

          <Card className="cursor-pointer hover:shadow-md transition-shadow">
            <Link to="/analytics" className="block p-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <BarChart3 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">View Analytics</h3>
                  <p className="text-sm text-muted-foreground">
                    Track usage and monitor performance
                  </p>
                </div>
              </div>
            </Link>
          </Card>
        </div>
      </motion.div>
    </div>
  )
}